import React from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, User, Users, BookOpen, LogOut, Plane, Home, Menu, LifeBuoy } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const UserDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const menuItems = [
    { path: '/user/dashboard', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/user/dashboard/profile', icon: User, label: 'My Profile' },
    { path: '/user/dashboard/passengers', icon: Users, label: 'My Passengers' },
    { path: '/user/dashboard/bookings', icon: BookOpen, label: 'My Bookings' },
    { path: '/user/dashboard/tickets', icon: LifeBuoy, label: 'Help & Tickets' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-0">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center">
                <Plane className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">ACF Tourism</h1>
                <p className="text-xs text-gray-500">User Dashboard</p>
              </div>
            </div>
            
            {/* Desktop: Logout & Home buttons */}
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Home className="w-5 h-5" />
                <span>Home</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </div>

            {/* Mobile: Home & Logout buttons */}
            <div className="md:hidden flex items-center gap-2">
              <button
                onClick={() => navigate('/')}
                className="p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Home"
              >
                <Home className="w-6 h-6" />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Desktop Sidebar - Hidden on mobile */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden sticky top-24">
              <div className="p-4 bg-gradient-to-br from-[#1B375B] to-[#659A9B]">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {user?.full_name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">
                      {user?.full_name || 'User'}
                    </p>
                    <p className="text-white/80 text-xs">
                      +91 {user?.phone || ''}
                    </p>
                  </div>
                </div>
              </div>

              <nav className="p-2">
                {menuItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.exact}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors mb-1 ${
                        isActive
                          ? 'bg-red-50 text-red-700 font-semibold'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`
                    }
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </nav>

              <div className="p-3 border-t border-gray-200">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  <LogOut className="w-5 h-5" />
                  Logout
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <Outlet />
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation - Only on mobile */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 shadow-lg">
        <div className="grid grid-cols-5 gap-1 p-2">
          <button
            onClick={() => navigate('/')}
            className="flex flex-col items-center justify-center py-2 px-1 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Home className="w-5 h-5 text-gray-600 mb-1" />
            <span className="text-[10px] text-gray-600 font-medium">Home</span>
          </button>

          {menuItems.filter((m) => !m.exact).map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="flex flex-col items-center justify-center py-2 px-1 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Icon className="w-5 h-5 text-gray-600 mb-1" />
                <span className="text-[10px] text-gray-600 font-medium truncate w-full text-center">
                  {item.label.replace('My ', '').replace('Help & ', '')}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default UserDashboard;
